import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, BookOpen, Share2, Bookmark, Volume2 } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import DharmaShareCard from '../components/DharmaShareCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DailyDharmaReadPage() {
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [reading, setReading] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareCard, setShowShareCard] = useState(false);

  useEffect(() => {
    const fetchReading = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('dharmbook_token');
        // First get today's quote
        const todayRes = await axios.get(`${API}/daily-dharma/today`);
        setQuote(todayRes.data);

        // Then get the full reading
        const readRes = await axios.post(
          `${API}/daily-dharma/read-full`,
          { quote_id: todayRes.data.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setQuote(readRes.data.quote);
        setReading(readRes.data.reading);
      } catch (err) {
        console.error('Error fetching daily dharma reading:', err);
        setError('Unable to load the reading. Please try again.');
      }
      setLoading(false);
    };
    fetchReading();
  }, []);

  const handleListen = () => {
    if (quote?.youtube_url) {
      window.open(quote.youtube_url, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F3]" data-testid="daily-dharma-read-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gradient-to-r from-[#FF9933] to-[#E88A2D] shadow-md">
        <div className="flex items-center gap-3 px-4 py-3 max-w-3xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-white font-bold text-lg truncate">Daily Dharma</h1>
            <p className="text-white/70 text-xs">Full Spiritual Reading</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowShareCard(true)}
              className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              data-testid="share-header-btn"
              title="Share this verse"
            >
              <Share2 className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={handleListen}
              className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              data-testid="listen-header-btn"
              title="Listen on YouTube"
            >
              <Volume2 className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Quote Hero Card */}
        {quote && (
          <div className="relative rounded-2xl overflow-hidden mb-6 bg-gradient-to-br from-orange-600 via-amber-600 to-orange-700 shadow-xl" data-testid="dharma-quote-card">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-4 right-4 text-[120px] leading-none text-white/20" style={{ fontFamily: 'Noto Serif Devanagari, serif' }}>
                ॐ
              </div>
            </div>
            <div className="relative p-6 md:p-8">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4 text-orange-200" />
                <span className="text-orange-200 text-xs font-semibold tracking-widest uppercase">Today's Verse</span>
              </div>
              <p
                className="text-2xl md:text-3xl text-white font-bold mb-2 leading-snug"
                style={{ fontFamily: 'Noto Serif Devanagari, serif' }}
              >
                " {quote.sanskrit} "
              </p>
              <p className="text-orange-100 text-sm italic mb-1">{quote.transliteration}</p>
              <p className="text-white/90 text-base font-medium mb-4">{quote.meaning}</p>
              <div className="flex items-center justify-between">
                <span className="text-orange-200 text-xs">Source: {quote.source}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleListen}
                    className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm font-semibold flex items-center gap-2 transition-colors backdrop-blur-sm"
                    data-testid="listen-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Listen on YouTube
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reading Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4" data-testid="loading-state">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
              </div>
            </div>
            <div className="text-center">
              <p className="text-slate-700 font-semibold">Generating Spiritual Reading...</p>
              <p className="text-slate-500 text-sm mt-1">DharmGuru is contemplating the verse</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-red-100" data-testid="error-state">
            <p className="text-red-600 font-medium mb-2">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-semibold text-sm transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden" data-testid="reading-content">
            <div className="p-6 md:p-8">
              <div className="prose prose-slate prose-sm md:prose-base max-w-none
                prose-headings:text-orange-800 prose-headings:font-bold
                prose-h2:text-lg prose-h2:mt-6 prose-h2:mb-3 prose-h2:pb-2 prose-h2:border-b prose-h2:border-orange-100
                prose-p:leading-relaxed prose-p:text-slate-700
                prose-strong:text-orange-700
                prose-li:text-slate-700
                prose-ul:space-y-1
                prose-blockquote:border-l-orange-400 prose-blockquote:bg-orange-50/50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg
              ">
                <ReactMarkdown>{reading}</ReactMarkdown>
              </div>
            </div>

            {/* Actions Footer */}
            <div className="border-t border-stone-100 px-6 py-4 flex items-center justify-between bg-stone-50/50">
              <button
                onClick={handleListen}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full text-sm font-semibold transition-colors shadow-sm"
                data-testid="listen-footer-btn"
              >
                <ExternalLink className="w-4 h-4" />
                Listen Discourse
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowShareCard(true)}
                  className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-full transition-colors"
                  data-testid="share-btn"
                >
                  <Share2 className="w-5 h-5" />
                </button>
                <button className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-full transition-colors" data-testid="save-btn">
                  <Bookmark className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareCard && quote && (
        <DharmaShareCard quote={quote} onClose={() => setShowShareCard(false)} />
      )}
    </div>
  );
}
